import * as Yup from 'yup';

// max was made big so that it is essentially irrelevant unless you give stringValidation a max
const stringValidation = ({
  min = 0,
  max = 9999999999,
  pattern = '',
  patternMsg = 'Is not in correct format',
  required = true,
} = {}) =>
  Yup.string()
    .min(min, `Must be at least ${min} characters`)
    .matches(new RegExp(`${pattern}`), patternMsg)
    .max(max)
    .when([], {
      is: () => required,
      then: Yup.string().trim().required('Required'),
      otherwise: Yup.string().trim(),
    });

const companyNameValidation = ({ min = 4 } = {}) =>
  stringValidation({
    min,
  });

const usernameValidation = ({
  min = 4,
  pattern = '^(?=.*[A-Za-z])[A-Za-z0-9]*$',
  patternMsg = 'Special characters not allowed. Valid characters are A-Z a-z 0-9',
} = {}) =>
  stringValidation({
    min,
    pattern,
    patternMsg,
  });

const emailValidation = ({ max = 40 } = {}) =>
  Yup.string().email('Must be a valid email').max(max).required('Required');

const phoneValidation = ({
  pattern = '^[0-9]{3}-[0-9]{3}-[0-9]{4}$',
  patternMsg = '10 numbers, ex. "888-432-5555"',
} = {}) =>
  stringValidation({
    pattern,
    patternMsg,
  });

const cityValidation = ({ pattern = '^[a-zA-Z ]*$', patternMsg = 'Letters only' } = {}) =>
  stringValidation({
    pattern,
    patternMsg,
  });

const zipValidation = ({ min = 5, pattern = '^[0-9]*$', patternMsg = 'Numbers only' } = {}) =>
  stringValidation({
    min,
    pattern,
    patternMsg,
  });

const passwordValidation = ({
  min = 8,
  pattern = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$',
  patternMsg = 'At least one uppercase, one lowercase, and one digit',
} = {}) =>
  stringValidation({
    min,
    pattern,
    patternMsg,
  });

const confirmPasswordValidation = () => {
  return Yup.string().test('Passwords match', 'Passwords must match', function (value) {
    return this.parent.password === value;
  });
};

const fileValidation = () => Yup.mixed().required();

const arrayValidation = (yupObject, arrayLength = 0) => {
  if (arrayLength == 0) {
    return Yup.array().of(Yup.object().shape(yupObject));
  } else return Yup.array().of(Yup.object().shape(yupObject)).length(arrayLength);
};

/**
 * Creates a Yup schema that validates the input as a number with optional maximum and minimum value
 * @param {object} [props={}] - An object with properties to configure the validation
 * @param {number} [props.max] - The maximum allowed value for the number
 * @param {number} [props.min] - The minimum allowed value for the number
 * @returns {object} - The Yup validation schema
 */
const mustBeNumber = (props = {}) => {
  let schema = Yup.number().typeError('Numbers only').required('Required');
  if (props.max) {
    return schema.max(props.max, `Max value is ${props.max}`);
  }

  if (props.min) {
    return schema.min(props.min, `Min value is ${props.min}`);
  }

  return schema;
};

const mustBeWholeNumber = (props = {}) => {
  let schema = Yup.number()
    .integer('Must be a whole number')
    .required('Required')
    .positive('Must be a positive number');
  if (props.max) {
    return schema.max(props.max, `Max value is ${props.max}`);
  }

  return schema;
};

export {
  stringValidation,
  companyNameValidation,
  usernameValidation,
  emailValidation,
  phoneValidation,
  cityValidation,
  zipValidation,
  passwordValidation,
  confirmPasswordValidation,
  fileValidation,
  arrayValidation,
  mustBeNumber,
  mustBeWholeNumber,
};
